import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import {
  type LoadingState,
  withLoadingIndicator,
} from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { type Filters, type StpInstance, pick } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import {
  initialTechnologies,
  isTopologyGraphMetadata,
} from 'components/Graph/GraphState';
import { useEffect, useMemo, useState } from 'react';
import { useApi } from 'utils/request/useApi';
import { fetchStpInstances } from '../helpers/fetchStpInstances';
import { orderStpInstances } from './helpers/orderStpInstances';

import { clearStpInstances } from 'components/Graph/commands/clearStpInstances';
import { selectStpiCapableDeviceSns } from 'components/Graph/model/selectors/selectStpiCapableDeviceSns';
import { record } from 'fp-ts';
import { useTraceableDeps } from 'utils/hooks/useTraceableDeps';

const INSTANCES_PAGE_SIZE = 10;
const SELECTED_INSTANCES_PAGE_SIZE = 1e6;

const initialInstanceData = {
  instances: [],
  count: null,
};

// Intentionally in frontend because it's subset of table result
export interface StpInstanceData {
  rootHostname: string;
  id: string;
  rootId: string;
  vlanId: number;
  vlanName: string;
}

export interface StpInstanceState {
  instances: StpInstanceData[];
  count: number | null;
}

export interface StpInstancesHook {
  clearNotPresentInstances: () => void;
  handleLoadMoreInstances: () => void;
  hasData: boolean;
  hasSelectedInstances: boolean;
  instanceData: StpInstanceState;
  loadInstanceFirstPage: (filters?: Filters, nodesChanged?: boolean) => void;
  loadingState: LoadingState;
  notPresentSelectedInstances: StpInstance[];
  visibleAppliedInstances: StpInstance[];
}

export const useStpInstances = (): StpInstancesHook => {
  const [instanceData, setInstanceData] =
    useState<StpInstanceState>(initialInstanceData);
  const [notPresentSelectedInstances, setNotPresentSelectedInstances] =
    useState<StpInstance[]>([]);
  const [inputFilters, setInputFilters] = useState<Filters>({});
  const [hasData, setHasData] = useState<boolean>(false);
  const [loadingState, setLoadingState] = useLoadingState();
  const apiRequest = useApi().request;

  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    appliedStpInstances: (s) =>
      isTopologyGraphMetadata(s.graphMetadata)
        ? s.graphMetadata.parameters.technologies?.stpInstances
        : undefined,
    selectedInstancesCount: (s) =>
      s.technologies.selectedTechnologies.stpInstances?.instances.filter(
        ({ visible }) => visible,
      ).length,
    snapshotId: prop('snapshotId'),
    stpiCapableSns: selectStpiCapableDeviceSns,
    stpInstances: (s) =>
      s.technologies.selectedTechnologies.stpInstances ??
      initialTechnologies.stpInstances,
  });

  const visibleAppliedInstances = useMemo(
    () => ctxProps.appliedStpInstances?.instances.filter(prop('visible')) ?? [],
    [ctxProps.appliedStpInstances],
  );

  const hasSelectedInstances = useMemo(
    () => ctxProps.stpInstances.instances.filter(prop('visible')).length !== 0,
    [ctxProps.stpInstances.instances],
  );

  const storeNotPresentSelectedInstances = (instances: StpInstanceData[]) => {
    const filteredInstances = ctxProps.stpInstances.instances.filter(
      (selectedInstance) =>
        selectedInstance.visible &&
        !instances.some(
          ({ rootId, vlanId }) =>
            rootId === selectedInstance.rootId &&
            vlanId === selectedInstance.vlanId,
        ),
    );

    setNotPresentSelectedInstances(filteredInstances);
  };

  const clearNotPresentInstances = () => {
    ctxProps.dispatch(clearStpInstances, {
      instances: notPresentSelectedInstances,
    });
    setNotPresentSelectedInstances([]);
  };

  /**
   * We don't know whether the instance is still present in newly loaded graph, because we currently
   * can't read from data to what path the instance belongs. Therefore upon reload,
   * we need to fetch selected instances from backend and compare them with what is in state.
   *
   * If we need to save this request in future, possible solution may be to send information
   * about owning path(s) along with stp instances, so we will be able to build a list of selected
   * instances from state.
   */
  const loadSelectedInstances = async (filters?: Filters) => {
    if (
      ctxProps.stpiCapableSns.length === 0 ||
      visibleAppliedInstances.length === 0
    ) {
      return {
        instances: [],
        count: 0,
      };
    }

    const selectedInstancesFilter: Filters = {
      or: visibleAppliedInstances.map(({ rootId, vlanId }) => ({
        and: [{ rootId: ['eq', rootId] }, { vlanId: ['eq', vlanId] }],
      })),
    };

    const enrichedFilters: Filters = {
      members: ['containsAny', ctxProps.stpiCapableSns],
      ...selectedInstancesFilter,
      ...filters,
    };

    const instancesResult = await fetchStpInstances(
      apiRequest,
      0,
      SELECTED_INSTANCES_PAGE_SIZE,
      enrichedFilters,
      ctxProps.snapshotId,
    );

    return {
      instances: instancesResult.data,
      count: instancesResult._meta.count,
    };
  };

  const loadInstances = async (start: number, filters?: Filters) => {
    if (ctxProps.stpiCapableSns.length === 0) {
      return {
        instances: [],
        count: 0,
      };
    }

    const selectedInstancesFilter: Filters =
      visibleAppliedInstances.length > 0
        ? {
            and: visibleAppliedInstances.map(({ rootId, vlanId }) => ({
              or: [{ rootId: ['neq', rootId] }, { vlanId: ['neq', vlanId] }],
            })),
          }
        : {};

    const enrichedFilters: Filters = {
      members: ['containsAny', ctxProps.stpiCapableSns],
      ...selectedInstancesFilter,
      ...filters,
    };
    const instancesResult = await fetchStpInstances(
      apiRequest,
      start,
      INSTANCES_PAGE_SIZE,
      enrichedFilters,
      ctxProps.snapshotId,
    );

    return {
      instances: orderStpInstances(
        instancesResult.data,
        visibleAppliedInstances,
      ),
      count: instancesResult._meta.count,
    };
  };

  const handleLoadMoreInstances = async () => {
    const newInstanceData = await loadInstances(
      instanceData.instances.length,
      inputFilters,
    );

    setInstanceData((instanceDataAfter) => {
      if (instanceDataAfter === instanceData) {
        return {
          count: newInstanceData.count,
          instances: [...instanceData.instances, ...newInstanceData.instances],
        };
      }
      return instanceDataAfter;
    });
  };

  const loadInstanceFirstPage = async (
    filters?: Filters,
    nodesChanged?: boolean,
  ) => {
    setInstanceData(initialInstanceData);

    setInputFilters(filters ?? {});

    await withLoadingIndicator(setLoadingState)(async () => {
      const loadedInstanceData = await loadInstances(0, filters);

      if (loadedInstanceData.count > 0) {
        setHasData(true);
      } else {
        if (nodesChanged) {
          storeNotPresentSelectedInstances([]);
        }
        setInstanceData({
          instances: [],
          count: 0,
        });
      }

      const loadedSelectedInstanceData = await loadSelectedInstances(filters);
      if (loadedSelectedInstanceData.count > 0) {
        setHasData(true);
      }

      if (nodesChanged) {
        storeNotPresentSelectedInstances(loadedSelectedInstanceData.instances);
      }

      setInstanceData({
        count: loadedInstanceData.count,
        instances: [
          ...loadedSelectedInstanceData.instances,
          ...loadedInstanceData.instances,
        ],
      });
    });
  };

  const reloadInstancesDeps = useTraceableDeps(
    pick(['stpiCapableSns', 'snapshotId', 'appliedStpInstances'])(ctxProps),
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: special deps usage
  useEffect(() => {
    loadInstanceFirstPage(
      record.empty,
      reloadInstancesDeps.prev === null ||
        reloadInstancesDeps.prev.stpiCapableSns !== ctxProps.stpiCapableSns,
    );
  }, [reloadInstancesDeps.version]);

  return {
    clearNotPresentInstances,
    handleLoadMoreInstances,
    hasData,
    hasSelectedInstances,
    instanceData,
    loadInstanceFirstPage,
    loadingState,
    notPresentSelectedInstances,
    visibleAppliedInstances,
  };
};
